import { useState, useEffect, useMemo } from "react";
import { Lock, User, Mail, Phone, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { authService } from "@/lib/api/services/auth.service";
import { paymentService } from "@/lib/api/services/payment.service";
import { usePlanDetails } from "@/lib/api/hooks/useDashboard";
import type { PlanDetails } from "@/lib/api/types";
import { getPaymentMode, loadConfig } from "@/lib/config";

export type SubscriptionPlan = {
  id: number;
  name: string;
  price: number;
  features: string[];
};

/**
 * Transform API PlanDetails to SubscriptionPlan format
 */
export const transformPlanDetails = (planDetails: PlanDetails[]): SubscriptionPlan[] => {
  return planDetails.map((plan) => ({
    id: plan.planId,
    name: plan.planName,
    price: plan.planValue,
    features: plan.planFeatures
      .split(";")
      .map((feature) => feature.replace(/\r\n/g, " ").replace(/\n/g, " ").trim())
      .filter((feature) => feature.length > 0),
  }));
};

/**
 * Hook to get subscription plans (transformed from API)
 */
export const useSubscriptionPlans = () => {
  const { data: planDetailsData, isLoading, error } = usePlanDetails();

  const subscriptionPlans = useMemo(() => {
    if (!planDetailsData || planDetailsData.length === 0) {
      return [];
    }
    return transformPlanDetails(planDetailsData);
  }, [planDetailsData]);

  return {
    subscriptionPlans,
    isLoading,
    error,
  };
};

type PaymentFormProps = {
  selectedPlanId: string;
  onPlanChange: (planId: string) => void;
};

const PaymentForm = ({ selectedPlanId, onPlanChange }: PaymentFormProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMode, setPaymentMode] = useState<"sandbox" | "production">("sandbox");
  const [formData, setFormData] = useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
  });
  const [errors, setErrors] = useState<{
    customerName?: string;
    customerEmail?: string;
    customerPhone?: string;
    subscriptionPlan?: string;
  }>({});
  const [isFieldsDisabled, setIsFieldsDisabled] = useState({
    customerName: false,
    customerEmail: false,
    customerPhone: false,
  });

  // Fetch plan details from API
  const { subscriptionPlans, isLoading: isLoadingPlans, error: planDetailsError } = useSubscriptionPlans();

  // Load payment mode from config
  useEffect(() => {
    const loadPaymentMode = async () => {
      try {
        await loadConfig();
        const mode = getPaymentMode();
        setPaymentMode(mode);
      } catch (error) {
        console.warn("Failed to load payment mode from config, using default:", error);
        setPaymentMode(paymentMode);
      }
    };
    loadPaymentMode();
  }, []);

  // Auto-fill form fields from user data on mount
  useEffect(() => {
    const loadUserData = () => {
      // Try to get user from authService first
      let user = authService.getCurrentUser();
      
      // Fallback: read directly from localStorage if authService doesn't return it
      if (!user) {
        try {
          const userStr = localStorage.getItem("user");
          if (userStr) {
            user = JSON.parse(userStr);
          }
        } catch (error) {
          console.error("PaymentForm: Error parsing user data from localStorage:", error);
        }
      }
      
      console.log("PaymentForm: User data loaded:", user);
      
      if (user) {
        // Extract values, handling both direct properties and potential nested structures
        const directorName = (user.directorName || "").trim();
        const email = (user.email || "").trim();
        const mobileNo = (user.mobileNo || "").trim();
        
        console.log("PaymentForm: Extracted values - directorName:", directorName, "email:", email, "mobileNo:", mobileNo);
        
        const hasDirectorName = directorName.length > 0;
        const hasEmail = email.length > 0;
        const hasMobileNo = mobileNo.length > 0;
        
        // Update form data if we have any values
        if (hasDirectorName || hasEmail || hasMobileNo) {
          setFormData({
            customerName: directorName,
            customerEmail: email,
            customerPhone: mobileNo,
          });
          
          setIsFieldsDisabled({
            customerName: hasDirectorName,
            customerEmail: hasEmail,
            customerPhone: hasMobileNo,
          });
          
          console.log("PaymentForm: Form data updated:", {
            customerName: directorName,
            customerEmail: email,
            customerPhone: mobileNo,
            disabled: {
              customerName: hasDirectorName,
              customerEmail: hasEmail,
              customerPhone: hasMobileNo,
            },
          });
        } else {
          console.log("PaymentForm: User data found but no directorName, email, or mobileNo available");
        }
      } else {
        console.log("PaymentForm: No user data found in localStorage");
      }
    };
    
    loadUserData();
  }, []);

  const selectedPlan = subscriptionPlans.find(
    (plan) => plan.id.toString() === selectedPlanId
  );

  const subtotal = selectedPlan?.price || 0;
  const tax = Math.round(subtotal * 0.18 * 100) / 100;
  const total = Math.round((subtotal + tax) * 100) / 100;

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    return phone.length === 10 && /^[0-9]+$/.test(phone);
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Validate customer name
    if (!formData.customerName.trim()) {
      newErrors.customerName = "Customer name is required";
    } else if (formData.customerName.trim().length < 2) {
      newErrors.customerName = "Customer name must be at least 2 characters";
    }

    // Validate customer email
    if (!formData.customerEmail.trim()) {
      newErrors.customerEmail = "Customer email is required";
    } else if (!validateEmail(formData.customerEmail)) {
      newErrors.customerEmail = "Please enter a valid email address";
    }

    // Validate customer phone
    if (!formData.customerPhone.trim()) {
      newErrors.customerPhone = "Customer phone is required";
    } else if (!validatePhone(formData.customerPhone)) {
      newErrors.customerPhone = "Please enter a valid 10-digit phone number";
    }

    // Validate subscription plan
    if (!selectedPlanId || !selectedPlan) {
      newErrors.subscriptionPlan = "Please select a subscription plan";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let formattedValue = value;
    
    // Only allow numbers for phone
    if (name === "customerPhone") {
      formattedValue = value.replace(/[^0-9]/g, "").substring(0, 10);
    }
    
    setFormData((prev) => ({ ...prev, [name]: formattedValue }));
    
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handlePlanChange = (value: string) => {
    onPlanChange(value);
    // Clear subscription plan error when user selects a plan
    if (errors.subscriptionPlan) {
      setErrors((prev) => ({ ...prev, subscriptionPlan: undefined }));
    }
  };

  // Listen for payment completion messages from Cashfree checkout window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security (adjust if needed)
      if (event.origin !== window.location.origin && !event.origin.includes('cashfree.com')) {
        return;
      }
      
      // Handle payment success callback
      if (event.data && event.data.type === 'CASHFREE_PAYMENT_SUCCESS') {
        const orderId = event.data.orderId || sessionStorage.getItem("pendingPaymentOrderId");
        if (orderId) {
          sessionStorage.removeItem("pendingPaymentOrderId");
          navigate(`/success?orderId=${encodeURIComponent(orderId)}`);
        }
      }
    };

    // Handle window focus - check if payment was completed
    const handleFocus = () => {
      const pendingOrderId = sessionStorage.getItem("pendingPaymentOrderId");
      const paymentCompleted = sessionStorage.getItem("paymentCompleted");
      
      // If payment was marked as completed, navigate to success page
      if (paymentCompleted === "true" && pendingOrderId) {
        sessionStorage.removeItem("pendingPaymentOrderId");
        sessionStorage.removeItem("paymentCompleted");
        navigate(`/success?orderId=${encodeURIComponent(pendingOrderId)}`);
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('focus', handleFocus);
    };
  }, [navigate]);

  const handleCashfreePayment = (paymentSessionId: string, paymentLink: string | null, orderId?: string) => {
    // If payment link is provided, redirect directly
    if (paymentLink) {
      // Store orderId for redirect handling
      if (orderId) {
        sessionStorage.setItem("pendingPaymentOrderId", orderId);
      }
      window.location.href = paymentLink;
      return;
    }

    // Validate paymentSessionId exists (but don't modify it)
    if (!paymentSessionId || String(paymentSessionId).length === 0) {
      setIsProcessing(false);
      toast({
        title: "Payment Error",
        description: "Invalid payment session ID",
        variant: "destructive",
      });
      return;
    }

    // Use the EXACT paymentSessionId - DO NOT modify/trim it
    // Cashfree requires the exact payment_session_id as returned by their API
    const sessionId = String(paymentSessionId);
    
    console.log("=== Cashfree Checkout Initialization ===");
    console.log("Session ID to use:", sessionId);
    console.log("Session ID type:", typeof sessionId);
    console.log("Session ID length:", sessionId.length);
    console.log("Session ID starts with 'session_':", sessionId.startsWith("session_"));

    // Final validation before sending to Cashfree
    if (!sessionId || sessionId.length === 0) {
      console.error("Empty session ID provided to Cashfree");
      setIsProcessing(false);
      toast({
        title: "Payment Error",
        description: "Invalid payment session ID",
        variant: "destructive",
      });
      return;
    }

    if (!sessionId.startsWith("session_")) {
      console.error("Session ID does not start with 'session_':", sessionId);
      setIsProcessing(false);
      toast({
        title: "Payment Error",
        description: "Invalid payment session ID format",
        variant: "destructive",
      });
      return;
    }

    // Use Cashfree Checkout.js SDK
    const initializeCashfree = () => {
      try {
        // Check if Cashfree SDK is available
        if (typeof (window as any).Cashfree === "undefined") {
          console.error("Cashfree SDK not loaded");
          setIsProcessing(false);
          toast({
            title: "Payment Error",
            description: "Payment gateway not available. Please refresh and try again.",
            variant: "destructive",
          });
          return;
        }

        // Initialize Cashfree
        // IMPORTANT: Mode must match the backend environment
        // Mode is loaded from config.json (can be modified after build)
        const cashfree = (window as any).Cashfree({
          mode: paymentMode, // Using mode from config.json
        });
        
        console.log("Cashfree SDK initialized with mode:", paymentMode);
        console.log("Checkout function available:", typeof cashfree.checkout === "function");

        // Open checkout using EXACT paymentSessionId (no modifications)
        if (cashfree && typeof cashfree.checkout === "function") {
          // Store orderId for payment completion callback
          if (orderId) {
            sessionStorage.setItem("pendingPaymentOrderId", orderId);
            console.log("Stored orderId for payment completion:", orderId);
          }
          
          // Use the EXACT sessionId as received from backend - DO NOT modify
          const checkoutParams: any = {
            paymentSessionId: sessionId, // EXACT value from backend
           // redirectTarget: "_blank", // Opens in new tab
          };
          
          // Add redirect URL for payment completion (Cashfree will redirect here after payment)
          // The backend should be configured to redirect to this URL with orderId
          const successUrl = `${window.location.origin}/success${orderId ? `?orderId=${encodeURIComponent(orderId)}` : ''}`;
          
          // Note: Cashfree redirect URL is typically configured in backend
          // But we can also try to set it here if the SDK supports it
          // For now, we'll rely on backend redirect configuration
          // and use sessionStorage as fallback
          
          console.log("Payment checkout will redirect to:", successUrl);
          console.log("OrderId stored:", orderId);
          
          console.log("=== Calling Cashfree Checkout ===");
          console.log("Checkout params:", JSON.stringify(checkoutParams, null, 2));
          console.log("PaymentSessionId being sent (EXACT):", sessionId);
          console.log("PaymentSessionId length:", sessionId.length);
          
          try {
            // Call Cashfree checkout with exact paymentSessionId
            const checkoutPromise = cashfree.checkout(checkoutParams);
            
            // If checkout returns a promise, handle it
            if (checkoutPromise && typeof checkoutPromise.then === "function") {
              checkoutPromise
                .then(() => {
                  console.log("✅ Cashfree checkout opened successfully");
                })
                .catch((error: any) => {
                  console.error("❌ Cashfree checkout promise rejected:", error);
                  console.error("Error details:", {
                    message: error?.message,
                    code: error?.code,
                    type: error?.type,
                    fullError: error,
                  });
                  setIsProcessing(false);
                  toast({
                    title: "Payment Error",
                    description: error?.message || "Payment gateway error occurred. Please check if backend environment (sandbox/production) matches frontend mode.",
                    variant: "destructive",
                  });
                });
            } else {
              console.log("✅ cashfree.checkout called (no promise returned)");
            }
          } catch (checkoutError: any) {
            console.error("❌ Error calling cashfree.checkout:", checkoutError);
            console.error("Error details:", {
              message: checkoutError?.message,
              code: checkoutError?.code,
              type: checkoutError?.type,
              stack: checkoutError?.stack,
              name: checkoutError?.name,
              fullError: checkoutError,
            });
            setIsProcessing(false);
            toast({
              title: "Payment Error",
              description: checkoutError?.message || "Failed to open payment gateway. Please verify backend environment matches frontend mode (sandbox/production).",
              variant: "destructive",
            });
          }
        } else {
          console.error("Cashfree checkout method not available", {
            cashfree: cashfree,
            hasCheckout: cashfree && typeof cashfree.checkout === "function",
            cashfreeType: typeof cashfree,
          });
          throw new Error("Cashfree checkout method not available");
        }
      } catch (error: any) {
        console.error("Cashfree initialization error:", error);
        setIsProcessing(false);
        toast({
          title: "Payment Error",
          description: error?.message || "Failed to initialize payment gateway. Please try again.",
          variant: "destructive",
        });
      }
    };

    // Check if script is already loaded
    if ((window as any).Cashfree) {
      initializeCashfree();
      return;
    }

    // Load Cashfree Checkout.js script dynamically
    const script = document.createElement("script");
    script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    script.async = true;
    script.onload = () => {
      // Wait a moment for the script to fully initialize
      setTimeout(() => {
        initializeCashfree();
      }, 100);
    };
    script.onerror = () => {
      setIsProcessing(false);
      toast({
        title: "Payment Error",
        description: "Failed to load payment gateway. Please check your internet connection and try again.",
        variant: "destructive",
      });
    };
    document.body.appendChild(script);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);

    try {
      const user = authService.getCurrentUser();
      const firmId = user?.firmId ; 

      const paymentData = {
        firmId: firmId,
        amount: Number(total.toFixed(2)),
        currency: "INR",
        customerName: formData.customerName.trim(),
        customerEmail: formData.customerEmail.trim(),
        customerPhone: formData.customerPhone.trim(),
        subscriptionPlanId: selectedPlan.id,
        subscriptionPlanName: selectedPlan.name.toLowerCase(),
      };

      // Call the payment API
      console.log("=== PaymentForm: Calling Payment API ===");
      console.log("Payment Data being sent:", JSON.stringify(paymentData, null, 2));
      
      const response = await paymentService.createOrder(paymentData);

      console.log("=== PaymentForm: Received API Response ===");
      console.log("Full Response:", JSON.stringify(response, null, 2));
      console.log("Response Type:", typeof response);
      console.log("Response Keys:", Object.keys(response || {}));
      console.log("Response Success:", response?.success);
      console.log("Response PaymentSessionId:", response?.paymentSessionId);
      console.log("Response PaymentLink:", response?.paymentLink);
      console.log("Response OrderId:", response?.orderId);
      console.log("Response Message:", response?.message);

      // Check if response has the expected structure
      if (!response) {
        setIsProcessing(false);
        toast({
          title: "Payment Error",
          description: "No response received from payment server",
          variant: "destructive",
        });
        return;
      }

      if (response.success && response.paymentSessionId) {
        // Use the EXACT paymentSessionId from backend - DO NOT modify it
        // Cashfree requires the exact payment_session_id as returned by their API
        const paymentSessionId = String(response.paymentSessionId);
        
        if (!paymentSessionId || paymentSessionId.length === 0) {
          setIsProcessing(false);
          toast({
            title: "Payment Error",
            description: "Invalid payment session ID received from server",
            variant: "destructive",
          });
          return;
        }

        console.log("=== PaymentSessionId from Backend (EXACT - NO MODIFICATIONS) ===");
        console.log("PaymentSessionId:", paymentSessionId);
        console.log("Length:", paymentSessionId.length);
        console.log("Type:", typeof paymentSessionId);
        console.log("Payment Link:", response.paymentLink);
        console.log("Order ID:", response.orderId);
        console.log("Gateway Order ID:", response.gatewayOrderId);
        
        // Handle Cashfree payment gateway with EXACT paymentSessionId (no cleaning/modification)
        handleCashfreePayment(paymentSessionId, response.paymentLink, response.orderId);
      } else {
        setIsProcessing(false);
        toast({
          title: "Payment Error",
          description: response.message || "Failed to create payment order. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      setIsProcessing(false);
      toast({
        title: "Payment failed",
        description: error?.message || "Please try again later.",
        variant: "destructive",
      });
    }
  };

  // Show loading state while fetching plans
  if (isLoadingPlans) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex flex-col items-center gap-2">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  // Show error state if plans failed to load
  if (planDetailsError) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <p className="text-sm text-destructive mb-2">Failed to load subscription plans</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Show message if no plans available
  if (!subscriptionPlans || subscriptionPlans.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-sm text-muted-foreground">No subscription plans available</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
      <p className="text-xs sm:text-sm text-muted-foreground mb-2">
        <span className="text-destructive">*</span> All fields are mandatory
      </p>
      
      <div className="space-y-3 sm:space-y-4">
        <div className="space-y-2">
          <Label htmlFor="customerName" className="text-sm sm:text-base text-foreground">
            Customer Name <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="customerName"
              name="customerName"
              type="text"
              placeholder="John Doe"
              value={formData.customerName}
              onChange={handleInputChange}
              className={`pl-10 h-10 sm:h-11 ${errors.customerName ? "border-destructive focus-visible:ring-destructive" : ""}`}
              disabled={isFieldsDisabled.customerName}
              required
            />
          </div>
          {errors.customerName && (
            <p className="text-xs sm:text-sm text-destructive mt-1">{errors.customerName}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="customerEmail" className="text-sm sm:text-base text-foreground">
            Customer Email <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="customerEmail"
              name="customerEmail"
              type="email"
              placeholder="john.doe@example.com"
              value={formData.customerEmail}
              onChange={handleInputChange}
              className={`pl-10 h-10 sm:h-11 ${errors.customerEmail ? "border-destructive focus-visible:ring-destructive" : ""}`}
              disabled={isFieldsDisabled.customerEmail}
              required
            />
          </div>
          {errors.customerEmail && (
            <p className="text-xs sm:text-sm text-destructive mt-1">{errors.customerEmail}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="customerPhone" className="text-sm sm:text-base text-foreground">
            Customer Phone <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="customerPhone"
              name="customerPhone"
              type="tel"
              placeholder="9876543210"
              value={formData.customerPhone}
              onChange={handleInputChange}
              className={`pl-10 h-10 sm:h-11 ${errors.customerPhone ? "border-destructive focus-visible:ring-destructive" : ""}`}
              disabled={isFieldsDisabled.customerPhone}
              maxLength={10}
              required
            />
          </div>
          {errors.customerPhone && (
            <p className="text-xs sm:text-sm text-destructive mt-1">{errors.customerPhone}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="subscriptionPlan" className="text-sm sm:text-base text-foreground">
            Subscription Plan <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
            <Select
              value={selectedPlanId}
              onValueChange={handlePlanChange}
              required
            >
              <SelectTrigger className={`pl-10 h-10 sm:h-11 ${errors.subscriptionPlan ? "border-destructive focus:ring-destructive" : ""}`}>
                <SelectValue placeholder="Select a plan" />
              </SelectTrigger>
              <SelectContent>
                {subscriptionPlans.map((plan) => (
                  <SelectItem key={plan.id} value={plan.id.toString()}>
                    {plan.name} - ₹{plan.price.toLocaleString('en-IN')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {errors.subscriptionPlan && (
            <p className="text-xs sm:text-sm text-destructive mt-1">{errors.subscriptionPlan}</p>
          )}
        </div>
      </div>

      {selectedPlan && (
        <div className="pt-3 sm:pt-4 border-t border-border">
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <span className="text-sm sm:text-base text-muted-foreground">Subtotal</span>
            <span className="text-sm sm:text-base font-medium text-foreground">₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <span className="text-sm sm:text-base text-muted-foreground">Tax (18%)</span>
            <span className="text-sm sm:text-base font-medium text-foreground">₹{tax.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between items-center text-base sm:text-lg font-semibold">
            <span className="text-foreground">Total</span>
            <span className="text-foreground">₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>
      )}

      <Button
        type="submit"
        className="w-full gradient-primary hover:opacity-90 transition-opacity h-11 sm:h-12 text-sm sm:text-base font-medium"
        disabled={isProcessing || !selectedPlan || !formData.customerName.trim() || !formData.customerEmail.trim() || !formData.customerPhone.trim()}
      >
        {isProcessing ? (
          <span className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
            Processing...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            {selectedPlan ? `Pay ₹${total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "Select a plan to continue"}
          </span>
        )}
      </Button>

      <p className="text-xs text-center text-muted-foreground">Your payment is secured with 256-bit SSL encryption</p>
    </form>
  );
};

export default PaymentForm;

